import { useEffect, useState } from "react";
import { Loader2, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiDelete } from "@/lib/apiAdmin";
import { SITE_FIELDS } from "@/lib/siteContent";
import { HUD } from "./theme";
import { JarvisPortfolio } from "./JarvisPortfolio";

const inputCls = `w-full rounded-xl px-3 py-2 text-sm border ${HUD.input}`;

export const JarvisWebsite = () => {
  const [vals, setVals] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/site/content").then((r) => setVals(r.data.content || {})); }, []);
  const save = async () => {
    setSaving(true);
    try {
      const body = Object.fromEntries(SITE_FIELDS.filter((f) => vals[f.key] !== undefined && vals[f.key] !== "").map((f) => [f.key, vals[f.key]]));
      await api.put("/admin/site/content", body);
      toast.success("Website updated — refresh the homepage to see it");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };
  const reset = async (key) => {
    await apiDelete(`/admin/site/content/${key}`);
    const { [key]: _, ...rest } = vals;
    setVals(rest);
    toast.success("Back to default");
  };
  if (!vals) return <Loader2 className="w-6 h-6 animate-spin text-cyan-300" />;
  return (
    <div className="space-y-4 max-w-3xl" data-testid="jarvis-website">
      <div className={`${HUD.panel} p-5`}>
        <p className="text-white font-['Outfit'] text-lg">Website editor</p>
        <p className="text-xs text-gray-400">Edit your homepage (incl. the “Showroom Detailing” headline) and section headings. Wrap a word in *stars* to make it glow blue. Services & prices live in the Services tab — or just tell JARVIS by text or call.</p>
      </div>
      <div className={`${HUD.panel} p-5 space-y-4`}>
        {SITE_FIELDS.filter((f) => !f.hidden).map((f) => (
          <label key={f.key} className="block space-y-1.5">
            <span className={`${HUD.label} flex items-center justify-between`}>
              {f.label}
              {vals[f.key] && <button type="button" onClick={() => reset(f.key)} className="text-gray-500 hover:text-white normal-case tracking-normal flex items-center gap-1" data-testid={`site-reset-${f.key}`}><RotateCcw className="w-3 h-3" /> default</button>}
            </span>
            {f.long ? (
              <textarea rows={3} value={vals[f.key] ?? ""} placeholder={f.def} onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })} data-testid={`site-field-${f.key}`} className={inputCls} />
            ) : (
              <input value={vals[f.key] ?? ""} placeholder={f.def || (f.image ? "https://…jpg" : "")} onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })} data-testid={`site-field-${f.key}`} className={inputCls} />
            )}
            {f.image && vals[f.key] && <img src={vals[f.key]} alt="" className="h-24 rounded-lg object-cover" />}
          </label>
        ))}
      </div>
      <JarvisPortfolio />
      <div className="sticky bottom-4 flex justify-end">
        <button onClick={save} disabled={saving} data-testid="site-save" className={`${HUD.btn} h-11 px-6 flex items-center gap-2`}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Publish changes
        </button>
      </div>
    </div>
  );
};

export default JarvisWebsite;
